// ** Next Imports
import Head from 'next/head'
import { Router, useRouter } from 'next/router'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'

// ** Store Imports
import { store } from 'src/store'
import { Provider } from 'react-redux'

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import { CacheProvider } from '@emotion/react'
import type { EmotionCache } from '@emotion/cache'

// ** Config Imports
import 'src/configs/i18n'
import { defaultACLObj } from 'src/configs/acl'
import themeConfig from 'src/configs/themeConfig'

// ** Fake-DB Import
import 'src/@fake-db'

// ** Third Party Import
import { Toaster } from 'react-hot-toast'

// ** Component Imports
import UserLayout from 'src/layouts/UserLayout'
import AclGuard from 'src/@core/components/auth/AclGuard'
import ThemeComponent from 'src/@core/theme/ThemeComponent'

import WindowWrapper from 'src/@core/components/window-wrapper'

// ** Spinner Import

// ** Contexts
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'

// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'

import { ClerkProvider, RedirectToSignIn, SignedIn, SignedOut } from '@clerk/nextjs'

// ** Prismjs Styles
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** Spinner Import
import Spinner from 'src/@core/components/spinner'

// ** Global css styles
import '../../styles/globals.css'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import MeGuard from 'src/utils/frontend/MeGuard'

import { NextWebVitalsMetric } from 'next/app'

export function reportWebVitals(metric: NextWebVitalsMetric) {
  const url = process.env.NEXT_PUBLIC_AXIOM_INGEST_ENDPOINT

  if (!url) {
    return
  }

  const body = JSON.stringify({
    route: window.__NEXT_DATA__.page,
    ...metric
  })

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, body)
  } else {
    fetch(url, { body, method: 'POST', keepalive: true })
  }
}

// ** Extend App Props with Emotion
type ExtendedAppProps = AppProps & {
  Component: NextPage
  emotionCache: EmotionCache
}

const clientSideEmotionCache = createEmotionCache()

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

const client = new ApolloClient({
  cache: new InMemoryCache(),
  uri: `/api/graphql/`,
  defaultOptions: {
    watchQuery: { notifyOnNetworkStatusChange: true },
    query: { notifyOnNetworkStatusChange: true }
  }
})

const publicPages = ['/sign-in/[[...index]]', '/sign-up/[[...index]]']

// ** Configure JSS & ClassName
export default function App(props: ExtendedAppProps) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const { push, pathname } = useRouter()

  // Variables
  const getLayout = Component.getLayout ?? (page => <UserLayout>{page}</UserLayout>)

  const setConfig = Component.setConfig ?? undefined

  const aclAbilities = Component.acl ?? defaultACLObj

  return (
    <ApolloProvider client={client}>
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <Head>
            <title>{`Magothy Docs`}</title>
            <meta name='description' content='Magothy River Technologies Documentation Site' />
            <meta name='keywords' content='Documentation Magothy River Technologies' />
            <meta name='viewport' content='initial-scale=1, width=device-width' />
          </Head>

          <ClerkProvider navigate={to => push(to)} {...pageProps}>
            <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
              <SettingsConsumer>
                {({ settings }) => {
                  return (
                    <ThemeComponent settings={settings}>
                      <WindowWrapper>
                        {publicPages.includes(pathname) ? (
                          <Component {...pageProps} />
                        ) : (
                          <>
                            <SignedIn>
                              <MeGuard>
                                <AclGuard aclAbilities={aclAbilities} guestGuard={false}>
                                  {getLayout(<Component {...pageProps} />)}
                                </AclGuard>
                              </MeGuard>
                            </SignedIn>
                            <SignedOut>
                              <RedirectToSignIn redirectUrl={'/sign-in'} />
                            </SignedOut>
                          </>
                        )}
                      </WindowWrapper>
                      <ReactHotToast>
                        <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                      </ReactHotToast>
                    </ThemeComponent>
                  )
                }}
              </SettingsConsumer>
            </SettingsProvider>
          </ClerkProvider>
        </CacheProvider>
      </Provider>
    </ApolloProvider>
  )
}
